import { useCallback, useEffect, useRef, useState } from 'react';
import { currentLocale } from '../../../../i18n';
import { speechLocale } from '../../../../lib/localePrefs';

type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string };
  }>;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function speechCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function speechRecognitionAvailable(): boolean {
  return speechCtor() != null;
}

const SILENCE_MS = 1200;

export function useSpeechAsk(opts: {
  onTranscript: (text: string) => void;
  onAutoSend: (text: string) => void;
  disabled?: boolean;
}) {
  const [listening, setListening] = useState(false);
  const [available] = useState(() => speechRecognitionAvailable());
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const silenceRef = useRef<number | null>(null);
  const finalRef = useRef('');
  const onTranscriptRef = useRef(opts.onTranscript);
  const onAutoSendRef = useRef(opts.onAutoSend);
  onTranscriptRef.current = opts.onTranscript;
  onAutoSendRef.current = opts.onAutoSend;

  const clearSilence = () => {
    if (silenceRef.current != null) {
      window.clearTimeout(silenceRef.current);
      silenceRef.current = null;
    }
  };

  const stop = useCallback(() => {
    clearSilence();
    recRef.current?.stop();
    recRef.current = null;
    setListening(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const start = useCallback(() => {
    if (opts.disabled || !available) return;
    const Ctor = speechCtor();
    if (!Ctor) return;
    stop();
    const rec = new Ctor();
    rec.lang = speechLocale(currentLocale());
    rec.interimResults = true;
    rec.continuous = true;
    finalRef.current = '';
    rec.onresult = event => {
      let interim = '';
      let finalText = finalRef.current;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const piece = event.results[i][0]?.transcript ?? '';
        if (event.results[i].isFinal) {
          finalText = `${finalText} ${piece}`.trim();
        } else {
          interim = `${interim} ${piece}`.trim();
        }
      }
      finalRef.current = finalText;
      const shown = `${finalText} ${interim}`.trim();
      onTranscriptRef.current(shown);
      clearSilence();
      silenceRef.current = window.setTimeout(() => {
        const ready = finalRef.current.trim();
        stop();
        if (ready) onAutoSendRef.current(ready);
      }, SILENCE_MS);
    };
    rec.onerror = () => {
      stop();
    };
    rec.onend = () => {
      setListening(false);
    };
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      stop();
    }
  }, [available, opts.disabled, stop]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  return { available, listening, toggle, stop };
}
